import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    PLATFORM_ID,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { provideRouter, Router } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { firstValueFrom } from 'rxjs';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';

import {
    API_KEY,
    APP_DOCUMENTATION_BASE_URL,
    AUTH_BASE_URL,
    BASE_URL,
    FINGER_PRINT_API_KEY,
    IndexDBName,
    IS_PRODUCTION,
    X_FUNCTIONS_KEY,
} from './core/global/constants';
import { httpRequestInterceptor } from './core/interceptors/http-request.interceptor';
import { connectivityInterceptor } from './core/interceptors/retry-on-reconnect.interceptor';
import { AppInitService } from './core/services/app-init.service';
import { routes } from './app.routes';

const dbConfig: DBConfig = {
    name: 'SALSurveyDB',
    version: 1,
    objectStoresMeta: [{
        store: IndexDBName,
        storeConfig: {keyPath: 'key', autoIncrement: false},
        storeSchema: [
            { name: 'key', keypath: 'key', options: {unique: true} },
            { name: 'value', keypath: 'value', options: {unique: false} }
        ]
    }],
};

function provideSentryProviders(platformId: object): unknown[] {
    if (isPlatformServer(platformId)) {
        return [];
    }
    return [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                logErrors: true,
                showDialog: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            useClass: Sentry.TraceService,
            deps: [Router],
        },
    ];
}

function initSentry(appInitService: AppInitService) {
    try {
        const sentryDSN = appInitService.getConfigValueByKey('sentryDSN');
        const baseUrl = appInitService.getConfigValueByKey('baseUrl');

        if (!sentryDSN) {
            console.warn('Sentry DSN not configured, skipping Sentry initialization');
            return;
        }

        const integrations = [Sentry.browserTracingIntegration()];
        if (IS_PRODUCTION) {
            integrations.push(
                Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false })
            );
        }

        Sentry.init({
            environment: IS_PRODUCTION ? 'production' : 'development',
            dsn: sentryDSN,
            integrations,
            tracesSampleRate: 0.1,
            tracePropagationTargets: ['localhost', baseUrl],
            replaysSessionSampleRate: IS_PRODUCTION ? 0.1 : 0.0,
            replaysOnErrorSampleRate: IS_PRODUCTION ? 1.0 : 0.0,
        });
    } catch (error) {
        console.warn('Sentry initialization failed:', error);
    }
}

async function initializeApp(): Promise<void> {
    try {
        const appInitService = inject(AppInitService);
        await appInitService.loadConfig();
        initSentry(appInitService);
    } catch (error) {
        console.error('App initialization failed:', error);
    }
}

const providers: any = [
    {
        provide: BASE_URL,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('baseUrl') + '/genesis/';
        },
        deps: [AppInitService],
    },
    {
        provide: API_KEY,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('apiKey');
        },
        deps: [AppInitService],
    },
    {
        provide: AUTH_BASE_URL,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('baseUrl') + '/';
        },
        deps: [AppInitService],
    },
    {
        provide: X_FUNCTIONS_KEY,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('x-functions-key');
        },
        deps: [AppInitService],
    },
    {
        provide: APP_DOCUMENTATION_BASE_URL,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('DocumentationBaseUrl');
        },
        deps: [AppInitService],
    },
    {
        provide: FINGER_PRINT_API_KEY,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('fingerPrintApiKey');
        },
        deps: [AppInitService],
    },
    {
        provide: 'sentryProviders',
        useFactory: provideSentryProviders,
        deps: [PLATFORM_ID],
    },
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(() => {
            inject(TraceService);
            return initializeApp();
        }),
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(routes),
        provideAnimations(),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withInterceptors([httpRequestInterceptor, connectivityInterceptor])
        ),
        importProvidersFrom(
            NgxIndexedDBModule.forRoot(dbConfig),
        ),
        provideToastr(),
        provideServiceWorker('ngsw-worker.js', {
            enabled: true,
            registrationStrategy: 'registerWhenStable:30000'
        }),
        ...providers,
    ],
};
